import React, { useState, useEffect } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import AuthUser from "../../AuthUser";
import Global from "../../APIConfig";
import {
    Truck, LayoutDashboard, Package, Wallet, Warehouse, UserCircle,
    Code2, LogOut, PanelLeftClose, PanelLeftOpen, X
} from "lucide-react";

/**
 * Pram Express vendor portal — light sidebar.
 * NavLink for active-route highlighting; toggle collapses to an icon rail;
 * mobile uses an off-canvas drawer controlled from the topbar via a
 * window 'px:toggle-nav' event.
 */
function LeftPanel() {
    let navigate = useNavigate();
    const { logout } = AuthUser();
    const [collapsed, setCollapsed] = useState(false);
    const [open, setOpen] = useState(false);
    let user = null;
    try { user = JSON.parse(localStorage.getItem('user')); } catch (e) { user = null; }

    useEffect(() => {
        const toggle = () => setOpen(o => !o);
        window.addEventListener('px:toggle-nav', toggle);
        return () => window.removeEventListener('px:toggle-nav', toggle);
    }, []);

    // Drive the layout width so the content area expands smoothly with the rail.
    useEffect(() => {
        document.documentElement.style.setProperty('--px-sidebar-w', collapsed ? '72px' : '260px');
    }, [collapsed]);

    const userlogout = () => {
        logout();
        navigate('/');
        window.location.reload();
    };

    const initials = (user && (user.name || '').trim())
        ? user.name.trim().charAt(0).toUpperCase()
        : 'P';

    const item = (to, icon, label, end) => (
        <NavLink to={to} end={end}
            className={({ isActive }) => 'px-nav__item' + (isActive ? ' px-nav__item--active' : '')}
            title={label}>
            <span className="px-nav__link">
                <span className="px-nav__icon">{icon}</span>
                <span className="px-nav__label-text">{label}</span>
            </span>
        </NavLink>
    );

    const group = (label, ...items) => (
        <div>
            <div className="px-nav__label">{label}</div>
            {items}
        </div>
    );

    return (
        <>
            {open && <div className="px-overlay" onClick={() => setOpen(false)}></div>}
            <aside className={`px-sidebar${collapsed ? ' px-sidebar--collapsed' : ''}${open ? ' px-sidebar--open' : ''}`}>
                <div className="px-sidebar__brand">
                    <a className="px-sidebar__logo" href="https://pramexpress.com/" target="_blank" rel="noopener noreferrer">
                        <img src="/assets/img/pram_logo.jpg" alt="Pram Express" />
                    </a>
                    <span className="px-sidebar__name">PRAM EXPRESS</span>
                    <button className="px-sidebar__collapse-btn" onClick={() => setCollapsed(c => !c)}
                        aria-label={collapsed ? "Expand sidebar" : "Collapse sidebar"}>
                        {collapsed ? <PanelLeftOpen size={18} /> : <PanelLeftClose size={18} />}
                    </button>
                </div>

                <nav className="px-sidebar__nav">
                    {group('Overview',
                        item('/Customer-Dash', <LayoutDashboard size={19} />, 'Dashboard', true))}

                    {group('Shipping',
                        item('/Shipping', <Package size={19} />, 'Shipping'))}

                    {group('Finance',
                        item('/Finance', <Wallet size={19} />, 'Finance'))}

                    {group('Operations',
                        item('/WarehouseList', <Warehouse size={19} />, 'Warehouses'))}

                    {group('Account',
                        item('/Profile', <UserCircle size={19} />, 'Profile'))}

                    {group('Developers',
                        item('/Developer', <Code2 size={19} />, 'API & Integration'))}
                </nav>

                <div className="px-user-card">
                    <span className="px-user-card__avatar">
                        {user && user.customer_image
                            ? <img src={`${Global.logoUrl}${user.customer_image}`} alt={user.name || 'profile'} />
                            : initials}
                    </span>
                    <span className="px-user-card__meta">
                        <span className="px-user-card__name">{user ? user.name : ''}</span>
                        <span className="px-user-card__company">{user ? user.company : ''}</span>
                    </span>
                    <button className="px-user-card__logout" onClick={userlogout} aria-label="Logout" title="Logout">
                        <LogOut size={18} />
                    </button>
                </div>
            </aside>
        </>
    );
}

export default LeftPanel;
